import React from 'react';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmailIcon from '@mui/icons-material/Email';
import setup from '../assets/data/setup.json';
import ResumeDownloadButtons from './ResumeDownloadButtons';
import TechBadge from './ui/TechBadge';
import HeroStats from './ui/HeroStats';

type Profile = typeof setup.profile & {
  heroOneLiner?: string;
  heroSubtitle?: string;
};

function PortfolioHero() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const profile = setup.profile as Profile;
  const heroBadges = setup.heroBadges || [];
  const subtitle =
    profile.heroSubtitle ||
    '4+ years building scalable products used by businesses across insurance, ERP, CRM, AI, workflow automation, and cloud infrastructure.';

  return (
    <section className="relative min-h-[88vh] flex items-center px-5 sm:px-8 lg:px-10 pt-28 pb-20">
      <div className="absolute inset-x-0 top-0 h-[480px] pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-muted/40 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-content w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p className="ui-eyebrow mb-5">{profile.name}</p>

          <h1 className="text-[2.5rem] sm:text-5xl lg:text-[3.25rem] font-semibold tracking-[-0.03em] text-ink leading-[1.08] mb-3">
            {profile.title}
          </h1>

          <p className="text-base sm:text-lg text-ink-muted font-medium mb-4 max-w-2xl">
            {profile.tagline}
          </p>

          <p className="text-base sm:text-lg text-ink-secondary leading-relaxed mb-8 max-w-2xl">
            {subtitle}
          </p>

          <HeroStats />

          {heroBadges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {heroBadges.map((badge) => (
                <TechBadge key={badge} label={badge} />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5 mb-10">
            <button onClick={() => scrollTo('case-studies')} className="ui-btn-primary">
              View Case Studies
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </button>
            <ResumeDownloadButtons variant="hero" />
            <button onClick={() => scrollTo('contact')} className="ui-btn-secondary">
              <EmailIcon sx={{ fontSize: 16 }} />
              Let&apos;s build together
            </button>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={profile.github}
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub"
              className="ui-btn-secondary !px-3 !py-2"
            >
              <GitHubIcon sx={{ fontSize: 18 }} />
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="ui-btn-secondary !px-3 !py-2"
            >
              <LinkedInIcon sx={{ fontSize: 18 }} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default PortfolioHero;
